/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Modal, Slider, Spin, Tag, Tooltip } from "antd";
import { useSnackbar } from "notistack";
import {
  Calendar,
  Clock,
  ShieldCheck,
  CreditCard,
  AlertCircle,
  CheckCircle2,
  Sparkles,
  Info,
  Layers,
} from "lucide-react";
import {
  getBackupRetention,
  updateBackupRetention,
} from "../../utils/organization";
import { getDefaultMethod, getPaymentMethods } from "../../utils/payment_methods";

const PRESET_DAYS = [
  { days: 7, label: "7 Days", extraDays: 0 },
  { days: 14, label: "14 Days", extraDays: 7 },
  { days: 21, label: "21 Days", extraDays: 14 },
  { days: 30, label: "30 Days", extraDays: 23 },
  { days: 60, label: "60 Days", extraDays: 53 },
];

const SLIDER_MARKS = {
  7: {
    style: { color: "#16a34a", fontWeight: "600" },
    label: "7d",
  },
  14: "14d",
  21: "21d",
  30: "30d",
  45: "45d",
  60: {
    style: { color: "#2563eb", fontWeight: "600" },
    label: "60d",
  },
};

const ExtendRetentionModal = ({
  open,
  onClose,
  organization,
  onSuccess,
  onAddNewCard,
  paymentCards: propPaymentCards,
  defaultPaymentMethodId: propDefaultPaymentMethodId,
}) => {
  const { getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentSavedDays, setCurrentSavedDays] = useState(7);
  const [selectedDays, setSelectedDays] = useState(7);
  const [pricePerDay, setPricePerDay] = useState(2.0);
  const [paymentCards, setPaymentCards] = useState([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null);

  // Initial base subscription plan & cost (fallback before API response)
  const computeInitialBaseCost = (org) => {
    if (!org) return 9.95;
    if (org.subscription_type === "inactive_orgs_sub" || org.is_vault_activated == 1) return 2.49;
    let base = 9.95;
    if (org.files == 1 || org.files === true || org.files === "1") base += 4.0;
    if (org.history_and_notes == 1 || org.history_and_notes === true || org.history_and_notes === "1") base += 4.0;
    return base;
  };

  const computeInitialBaseLabel = (org) => {
    if (!org) return "Accounting";
    if (org.subscription_type === "inactive_orgs_sub" || org.is_vault_activated == 1) return "Vault";
    let label = "Accounting";
    const mods = [];
    if (org.files == 1 || org.files === true || org.files === "1") mods.push("Files");
    if (org.history_and_notes == 1 || org.history_and_notes === true || org.history_and_notes === "1") mods.push("History & Notes");
    if (org.payroll == 1 || org.payroll === true || org.payroll === "1") mods.push("Payroll");
    return mods.length ? `${label} + ${mods.join(" + ")}` : label;
  };

  const [baseSubscriptionCost, setBaseSubscriptionCost] = useState(() =>
    computeInitialBaseCost(organization)
  );
  const [basePlanLabel, setBasePlanLabel] = useState(() =>
    computeInitialBaseLabel(organization)
  );

  // Sync base cost and label whenever organization prop changes
  useEffect(() => {
    if (organization) {
      setBaseSubscriptionCost(computeInitialBaseCost(organization));
      setBasePlanLabel(computeInitialBaseLabel(organization));
    }
  }, [organization]);

  const orgId = organization?.id;
  const orgName = organization?.account || organization?.name || "Organization";
  const provider = (organization?.provider || "xero").toLowerCase();
  const isQbo = provider === "qbo";
  const providerBrand = isQbo ? "QuickBooks Online" : "Xero";

  const extraDays = Math.max(0, selectedDays - 7);
  const extraCost = extraDays * pricePerDay;
  const totalMonthlyCost = baseSubscriptionCost + extraCost;
  const isUnchanged = selectedDays === currentSavedDays;

  // Sync with prop payment cards if provided from parent
  useEffect(() => {
    if (propPaymentCards && propPaymentCards.length > 0) {
      setPaymentCards(propPaymentCards);
      const defId = propDefaultPaymentMethodId || null;
      if (defId) setDefaultPaymentMethodId(defId);
      if (defId && propPaymentCards.some((c) => c.id === defId)) {
        setSelectedPaymentMethodId(defId);
      } else if (!selectedPaymentMethodId && propPaymentCards.length > 0) {
        setSelectedPaymentMethodId(propPaymentCards[0].id);
      }
    }
  }, [propPaymentCards, propDefaultPaymentMethodId]);

  const loadRetentionAndCards = async () => {
    if (!orgId || !open) return;
    setLoading(true);
    try {
      const claims = await getIdTokenClaims();
      const token = claims?.__raw;

      // 1. Fetch current retention policy
      const retentionRes = await getBackupRetention(token, orgId, provider, {
        files: organization?.files,
        history_and_notes: organization?.history_and_notes,
      });
      if (retentionRes?.data?.success) {
        const days = retentionRes.data.current_days || 7;
        setCurrentSavedDays(days);
        setSelectedDays(days);
        if (retentionRes.data.price_per_extra_day) {
          setPricePerDay(Number(retentionRes.data.price_per_extra_day));
        }
        if (retentionRes.data.base_subscription_cost != null) {
          setBaseSubscriptionCost(Number(retentionRes.data.base_subscription_cost));
        }
        if (retentionRes.data.base_plan_label) {
          setBasePlanLabel(retentionRes.data.base_plan_label);
        }
      }

      // 2. Fetch saved payment methods and default payment method in parallel
      const [methodsRes, defaultRes] = await Promise.all([
        getPaymentMethods(token),
        getDefaultMethod(token),
      ]);

      const cards =
        methodsRes?.data?.cards?.data ||
        methodsRes?.data?.cards ||
        methodsRes?.data?.payment_methods ||
        [];
      const defId =
        defaultRes?.data?.default_method ||
        methodsRes?.data?.defaultId ||
        null;

      setPaymentCards(cards);
      setDefaultPaymentMethodId(defId);

      if (defId && cards.some((c) => c.id === defId)) {
        setSelectedPaymentMethodId(defId);
      } else if (cards.length > 0) {
        setSelectedPaymentMethodId(cards[0].id);
      } else {
        setSelectedPaymentMethodId(null);
      }
    } catch (err) {
      console.error("Error loading retention details:", err);
      enqueueSnackbar(
        err?.response?.data?.message || "Failed to load retention details.",
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadRetentionAndCards();
    }
  }, [open, orgId, provider]);

  const handleConfirm = async () => {
    if (extraCost > 0 && paymentCards.length === 0 && !selectedPaymentMethodId) {
      enqueueSnackbar(
        "Please add or select a payment card to purchase extended retention.",
        { variant: "warning" }
      );
      if (onAddNewCard) {
        onAddNewCard();
      }
      return;
    }

    setSubmitting(true);
    try {
      const claims = await getIdTokenClaims();
      const token = claims?.__raw;

      const payload = {
        org_id: orgId,
        provider,
        days: selectedDays,
        payment_method_id: selectedPaymentMethodId || undefined,
      };

      const res = await updateBackupRetention(token, payload);
      if (res?.data?.success) {
        enqueueSnackbar(
          res.data.message || "Backup retention policy updated successfully!",
          { variant: "success" }
        );
        setCurrentSavedDays(selectedDays);
        if (onSuccess) {
          onSuccess(selectedDays);
        }
        onClose();
      } else {
        enqueueSnackbar(
          res?.data?.message || "Failed to update backup retention policy.",
          { variant: "error" }
        );
      }
    } catch (err) {
      console.error("Error saving retention policy:", err);
      enqueueSnackbar(
        err?.response?.data?.message ||
          "An error occurred while updating the retention policy.",
        { variant: "error" }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      width={620}
      className="retention-modal"
      title={
        <div className="flex items-center gap-2.5 pb-2 border-b border-gray-100">
          <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
            <Clock className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900 m-0">
              Extend Rolling Backup Retention
            </h3>
            <p className="text-xs text-gray-500 m-0 mt-0.5">
              Keep your organization&apos;s backup data available for longer
            </p>
          </div>
        </div>
      }
    >
      {loading ? (
        <div className="py-16 text-center">
          <Spin size="large" />
          <p className="text-sm text-gray-500 mt-4">
            Loading retention details...
          </p>
        </div>
      ) : (
        <div className="py-4 space-y-5">
          {/* Organization Pill Banner */}
          <div className="flex items-center justify-between p-3 rounded-xl bg-gray-50 border border-gray-200">
            <div className="flex items-center gap-2.5">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  isQbo
                    ? "bg-green-100 text-green-800"
                    : "bg-sky-100 text-sky-800"
                }`}
              >
                {providerBrand}
              </span>
              <span className="font-semibold text-sm text-gray-900">
                {orgName}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <Layers className="w-3.5 h-3.5 text-gray-400" />
              <span>Current window:</span>
              <span className="font-semibold text-gray-800">
                {currentSavedDays} Days
              </span>
            </div>
          </div>

          {/* Quick Preset Selector Buttons */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Select Retention Window
              </label>
              <span className="text-xs text-gray-500">
                Range: 7 to 60 Days
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {PRESET_DAYS.map((preset) => {
                const isSelected = selectedDays === preset.days;
                return (
                  <button
                    key={preset.days}
                    type="button"
                    onClick={() => setSelectedDays(preset.days)}
                    className={`flex flex-col items-center justify-center p-2.5 rounded-xl border text-center transition-all cursor-pointer ${
                      isSelected
                        ? "border-blue-600 bg-blue-50/70 text-blue-900 ring-2 ring-blue-500/20 shadow-sm"
                        : "border-gray-200 bg-white hover:border-gray-300 text-gray-700"
                    }`}
                  >
                    <span className="text-sm font-bold">{preset.label}</span>
                    <span
                      className={`text-[11px] mt-0.5 font-medium ${
                        preset.extraDays === 0
                          ? "text-green-600 font-semibold"
                          : isSelected
                          ? "text-blue-700"
                          : "text-gray-500"
                      }`}
                    >
                      {preset.extraDays === 0
                        ? `$${baseSubscriptionCost.toFixed(2)}/mo`
                        : `+$${preset.extraDays * pricePerDay}/mo`}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Slider Component */}
          <div className="bg-gray-50/70 p-4 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-medium text-gray-600">
                Adjust days slider:
              </span>
              <div className="flex items-baseline gap-1">
                <span className="text-xl font-bold text-blue-600">
                  {selectedDays}
                </span>
                <span className="text-xs font-medium text-gray-500">
                  Days Retention
                </span>
              </div>
            </div>

            <Slider
              min={7}
              max={60}
              step={1}
              value={selectedDays}
              onChange={(val) => setSelectedDays(val)}
              marks={SLIDER_MARKS}
              tooltip={{
                formatter: (val) => `${val} Days`,
              }}
            />
          </div>

          {/* Pricing Breakdown Card */}
          <div className="rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50/50 to-indigo-50/30 p-4 space-y-3">
            <div className="flex items-center justify-between pb-2.5 border-b border-blue-100/70">
              <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
                <Calendar className="w-4 h-4 text-blue-600" />
                <div>
                  <span className="font-semibold text-gray-900 block">
                    Current Subscription ({basePlanLabel})
                  </span>
                  <span className="text-[11px] text-gray-500">
                    {baseSubscriptionCost === 17.95
                      ? "Base $9.95 + Files $4.00 + History & Notes $4.00 (includes 7-day retention)"
                      : baseSubscriptionCost === 13.95
                      ? "Base $9.95 + Add-on $4.00 (includes 7-day retention)"
                      : "Includes 7-day rolling backup retention"}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-xs font-bold text-gray-900 block">
                  ${baseSubscriptionCost.toFixed(2)}
                </span>
                <span className="text-[10px] text-green-700 font-medium bg-green-50 px-1.5 py-0.5 rounded border border-green-200">
                  Included
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between pb-2.5 border-b border-blue-100/70">
              <div className="flex items-center gap-2 text-xs font-medium text-gray-700">
                <Sparkles className="w-4 h-4 text-indigo-600" />
                <div>
                  <span className="font-semibold text-gray-900 block">
                    {extraDays > 0
                      ? `Extended Retention (+${extraDays} extra days)`
                      : "Extended Retention"}
                  </span>
                  <span className="text-[11px] text-gray-500">
                    {extraDays > 0
                      ? `${extraDays} extra days × $${pricePerDay.toFixed(2)}/day`
                      : "7 days retention (no extra days)"}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-xs font-bold text-gray-900">
                  {extraDays > 0 ? `+$${extraCost.toFixed(2)}` : "$0.00"}
                </span>
                <span className="text-xs text-gray-500 ml-1">/mo</span>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-white/90 border border-blue-200/90 shadow-xs">
              <div>
                <span className="text-xs font-bold text-gray-900 uppercase tracking-wide block">
                  Total Monthly Cost
                </span>
                <span className="text-[11px] text-gray-500">
                  Subscription (${baseSubscriptionCost.toFixed(2)}) + Extra retention (${extraCost.toFixed(2)})
                </span>
              </div>
              <div className="text-right">
                <span className="text-xl font-extrabold text-blue-700">
                  ${totalMonthlyCost.toFixed(2)}
                </span>
                <span className="text-xs font-semibold text-gray-500 ml-1">
                  /month
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between pt-0.5 text-xs">
              <span className="text-gray-600 font-medium">
                Payment Due Today:
              </span>
              <span className="font-bold">
                {extraCost > 0 ? (
                  <span className="text-blue-700">
                    ${extraCost.toFixed(2)}{" "}
                    <span className="text-gray-500 font-normal">
                      (only extra {extraDays} days charged)
                    </span>
                  </span>
                ) : (
                  <span className="text-green-600">
                    $0.00{" "}
                    <span className="text-gray-500 font-normal">
                      (included in subscription)
                    </span>
                  </span>
                )}
              </span>
            </div>
          </div>

          {/* Payment Method Selector (if cost > 0) */}
          {extraCost > 0 && (
            <div className="space-y-2 border-t border-gray-100 pt-3">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-gray-700 flex items-center gap-1.5">
                  <CreditCard className="w-3.5 h-3.5 text-gray-500" />
                  Payment Method
                </label>
                {onAddNewCard && (
                  <button
                    type="button"
                    onClick={onAddNewCard}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 cursor-pointer"
                  >
                    + Add New Card
                  </button>
                )}
              </div>

              {paymentCards.length > 0 ? (
                <select
                  value={selectedPaymentMethodId || ""}
                  onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs sm:text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {paymentCards.map((cardItem) => {
                    const cardObj = cardItem.card || cardItem;
                    const isDefault = cardItem.id === defaultPaymentMethodId;
                    const brand = (
                      cardObj.brand ||
                      cardObj.display_brand ||
                      "Card"
                    ).toUpperCase();
                    const last4 = cardObj.last4 || "";
                    const exp = `${cardObj.exp_month || ""}/${
                      cardObj.exp_year || ""
                    }`;
                    return (
                      <option key={cardItem.id} value={cardItem.id}>
                        {brand} •••• {last4} (exp {exp})
                        {isDefault ? " [Default]" : ""}
                      </option>
                    );
                  })}
                </select>
              ) : (
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-amber-800 text-xs flex items-center justify-between">
                  <span>No payment method on file for this account.</span>
                  {onAddNewCard && (
                    <Button
                      size="small"
                      type="primary"
                      onClick={onAddNewCard}
                      className="text-xs ml-2"
                    >
                      Add Card
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Retention Rules Note */}
          <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg text-gray-600 text-xs leading-relaxed">
            <Info className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
            <div>
              <span>
                <strong>Rolling Backup Policy:</strong> Automated daily backups
                older than <strong>{selectedDays} days</strong> will be
                automatically purged from cloud storage. If your subscription is
                ever paused or cancelled, your organization&apos;s data will
                remain protected for the full <strong>{selectedDays}-day</strong>{" "}
                grace period before final deletion.
              </span>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
            <Button onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleConfirm}
              loading={submitting}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isUnchanged
                ? "Keep Retention Window"
                : extraCost > 0
                ? `Confirm & Pay $${extraCost.toFixed(2)}`
                : "Save 7 Days Retention (Included)"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ExtendRetentionModal;
